import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { type IPhoneCall, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ApiClient } from '../../services/api';
import { queryKeys } from './queryKeys';

/**
 * Fetches a single phone-call record by id. Used by the focused view's
 * CallFromUnknown rescue flow (`SearchPanel` rescue mode) to resolve whether
 * an inbound call has a `companyId` association — if not, the rep needs the
 * search panel auto-opened so they can attach the call to a lead.
 *
 * Wraps `ApiClient.getPhoneCallById`, which returns an
 * `IOperationResult<IPhoneCall>` — we throw on failure so react-query
 * surfaces it as `error` state. Disabled when no id is provided.
 */
export function usePhoneCallById(phoneCallId: string | undefined): UseQueryResult<IPhoneCall, Error> {
	const userSession = useUserSession();

	return useQuery<IPhoneCall, Error>({
		queryKey: queryKeys.focusedView.phoneCall(phoneCallId),
		enabled: !!phoneCallId,
		queryFn: async () => {
			const api = new ApiClient(userSession);
			const result = await api.getPhoneCallById(phoneCallId as string);
			if (!result.success || !result.value) {
				throw asApiError(result);
			}
			return result.value;
		},
	});
}
